import { useQueryClient } from "@tanstack/react-query";
import { addError } from "@wayflyer/blackbox-fe";
import { CheckboxField, Form, useToast } from "@wayflyer/flyui";
import { ConnectionSchemas, CustomerwebSchemas } from "api-types";
import { axiosClient as client } from "auth-sdk";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { useCustomerAppConfig } from "undercarriage";
import { analytics } from "../../analytics";
import { AccountsForm } from "../../components";
import { accountTransformer, connectionPayloadBuilders } from "../../data-sources";

import { useConnectionFlowsConfig } from "../config";
import { useReportConnectionCompleted } from "../connection-completion";
import { MESSAGES } from "../messages";

export const PropertySelector = ({
  connectorSlug,
  completeConnectionProps,
}: {
  connectorSlug: string;
  completeConnectionProps: ConnectionSchemas.CompleteConnectionPropsSchema;
}) => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const queryClient = useQueryClient();
  const toaster = useToast();
  const reportConnectionCompleted = useReportConnectionCompleted();
  const { completeConnectionSubmitApiUrl, cancelConnectionSubmitApiUrl, connectorsApiUrl } = useConnectionFlowsConfig();
  const submitPaths = {
    completeConnectionPath: completeConnectionSubmitApiUrl.replace(":customerSlug", customerDomain),
    cancelConnectionPath: cancelConnectionSubmitApiUrl.replace(":customerSlug", customerDomain),
  };
  const [loading, setLoading] = useState(false);
  const transformedProps = completeConnectionProps
    ? accountTransformer(
        intl,
        completeConnectionProps,
        {},
        {
          //required but never used
          error: () => {
            return;
          },
        },
        submitPaths
      )
    : null;

  const [selectedAccounts, setSelectedAccounts] = useState(transformedProps?.accounts || []);
  const propertySelectionNotRequired =
    transformedProps?.accounts &&
    transformedProps?.accounts.length === 1 &&
    !Array.isArray(transformedProps?.accounts[0].properties);

  const [submitDisabled, setSubmitDisabled] = useState(
    !(selectedAccounts.length === 1 && selectedAccounts[0].selected)
  );

  /**
   * Confirms the selection, then moves the panel on in place. The URL swaps `complete-connection`
   * for `connectionSucceed` so the flow reads as finished, and the connector's properties are
   * refetched so the new connection appears in its list.
   */
  const handleConfirm = async () => {
    if (!transformedProps) {
      return;
    }
    setLoading(true);
    try {
      const payload = connectionPayloadBuilders[connectorSlug]?.(selectedAccounts) ?? {};
      await client.post<CustomerwebSchemas.CompleteCancelEmptySchema>(submitPaths.completeConnectionPath, {
        ...completeConnectionProps,
        ...payload,
      });

      analytics.updateOnboardingState(completeConnectionProps, {});

      const connectorBaseUrl = connectorsApiUrl.replace(":customerSlug", customerDomain);
      await queryClient.invalidateQueries({
        queryKey: [`${connectorBaseUrl.replace(/\/$/, "")}/${connectorSlug}`],
      });

      const completedParams = new URLSearchParams(searchParams);
      completedParams.delete("complete-connection");
      completedParams.set("connectionSucceed", connectorSlug);
      // Restate first-vs-reconnect from the props rather than leaving the OAuth return's own value
      // in place: the backend spells it `True`/`False`, while consumers match the `true`/`false` the
      // handler's own completion path produces.
      completedParams.set("first-connection", String(completeConnectionProps.firstConnection));
      setSearchParams(completedParams, { replace: true });
      reportConnectionCompleted(completedParams);
    } catch (error) {
      // Log the raw error for diagnostics; show the user a generic message.
      addError(error);
      toaster.critical(
        intl.formatMessage({
          id: "shared-ui: connection completion failed",
          defaultMessage: "Connection could not be completed. Please try again.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const cancelConnection = () => {
    client
      .post<CustomerwebSchemas.CompleteCancelEmptySchema>(submitPaths.cancelConnectionPath, completeConnectionProps)
      .then(() => setSearchParams({ platform: connectorSlug }));
  };

  return propertySelectionNotRequired ? (
    <Form
      onSubmit={handleConfirm}
      primaryAction={{
        content: intl.formatMessage(MESSAGES.connectLabel),
        disabled: submitDisabled,
        loading: loading,
      }}
      tertiaryAction={{
        content: intl.formatMessage(MESSAGES.cancelLabel),
        onAction: cancelConnection,
      }}
    >
      <CheckboxField
        name="account-property-1"
        options={{
          name: "account-property-1",
          label: (selectedAccounts.length === 1 && selectedAccounts[0].label) as string,
          defaultChecked: selectedAccounts.length === 1 && selectedAccounts[0].selected,
          onChange: () => {
            if (selectedAccounts.length === 1) {
              setSelectedAccounts([{ ...selectedAccounts[0], selected: !selectedAccounts[0].selected }]);
              setSubmitDisabled(selectedAccounts[0].selected);
            }
          },
        }}
      />
    </Form>
  ) : (
    <Form
      onSubmit={handleConfirm}
      primaryAction={{
        content: intl.formatMessage(MESSAGES.connectLabel),
        disabled: transformedProps?.confirmDisabled(selectedAccounts),
        loading: loading,
      }}
      tertiaryAction={{
        content: intl.formatMessage(MESSAGES.cancelLabel),
        onAction: cancelConnection,
      }}
    >
      <AccountsForm
        accounts={selectedAccounts}
        setAccounts={setSelectedAccounts}
        selectAllLabel={intl.formatMessage({
          id: "funding-application: default select all accounts",
          defaultMessage: "Select all",
        })}
      />
    </Form>
  );
};
